import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';

import AutocompleteCompanyProjects from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyProjects';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import SelectInput from '~components/FormFields/v2/SelectInput';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { Project } from '~hooks/useProjects/models';

export interface AddProjectLinkDto {
  project: AutocompleteFieldOption<Project> | null;
  workspaceKey: string;
}

interface VetaCenterAddProjectLinkFormProps {
  companyId: string;
  isMutating: boolean;
  linkedProjectIds: string[];
  onAdd: (formData: AddProjectLinkDto) => Promise<boolean>;
  workspaceOptions: { key: string; label: string }[];
}

// Parents remount this form when the workspace list changes so the default selection
// always points at a workspace that exists.
export default function VetaCenterAddProjectLinkForm({
  companyId,
  isMutating,
  linkedProjectIds,
  onAdd,
  workspaceOptions,
}: VetaCenterAddProjectLinkFormProps) {
  const { control, handleSubmit, reset, watch } = useForm<AddProjectLinkDto>({
    defaultValues: { project: null, workspaceKey: workspaceOptions[0]?.key ?? '' },
  });
  const [project, workspaceKey] = watch(['project', 'workspaceKey']);

  const handleAdd = handleSubmit(async (formData) => {
    const wasAdded = await onAdd(formData);
    if (wasAdded) reset({ project: null, workspaceKey: formData.workspaceKey });
  });

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        gap: 2,
        maxWidth: '900px',
        mt: 3,
      }}
    >
      <SelectInput
        control={control}
        isDisabled={isMutating}
        label={t('administration.integration.veta_center.projects.contract_workspace')}
        name="workspaceKey"
        options={workspaceOptions.map(({ key, label }) => ({ label, value: key }))}
      />
      <AutocompleteCompanyProjects
        companyId={companyId}
        control={control}
        excludedOptionValues={linkedProjectIds}
        isDisabled={isMutating}
        label={t('administration.integration.veta_center.projects.project_label')}
        name="project"
      />
      <SmallLoadingButton
        color="brandV2Yellow"
        disabled={!project || !workspaceKey}
        loading={isMutating}
        onClick={handleAdd}
        sx={{ flexShrink: 0 }}
      >
        {t('administration.integration.veta_center.cta.add_project')}
      </SmallLoadingButton>
    </Box>
  );
}
